import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Image } from '../../ui/media/Image';
import { addProtocol } from '../../utils/addProtocol';
import { buildImageSource, buildImageSourceSet } from '../../utils/buildImageSource';
import { useTranslation } from '../../utils/useTranslation';
import { IconClose } from '../icon/IconClose';
import { IconProduct } from '../icon/IconProduct';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { MediaCover } from '../media/MediaCover';
import { ProductItemUI } from '../posts/types';
import { Price } from '../price/Price';
import { Typography, TypographyProps } from '../typography/Typography';
import { ProductRemoveForm } from './ProductRemoveForm/ProductRemoveForm';

const Title = styled(Typography)
    .withConfig({ displayName: 'Title' })
    .attrs((props) => ({
        ...props,
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }))`
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const RemoveButton = styled(IconButton)
    .withConfig({
        displayName: 'RemoveButton',
    })
    .attrs((props) => ({
        ...props,
        position: 'absolute',
        top: 1.5,
        right: 1.5,
        width: 24,
        height: 24,
        borderRadius: 1,
    }))``;

interface ProductCardProps {
    product: ProductItemUI;
    canRemoveProduct?: boolean;
    onProductClick: () => void;
    onProductRemove: (productId: string) => void;
    hideOldPrice?: boolean;
    fintechDiscountOptions?: TypographyProps & {
        compact?: boolean;
    };
    showOldPriceBadge?: boolean;
}

export const ProductCard = ({
    product: { oldPriceInfo, priceInfo, content, title, url, id, fintechDiscount },
    onProductClick,
    onProductRemove,
    fintechDiscountOptions,
    hideOldPrice,
    showOldPriceBadge,
    canRemoveProduct,
}: ProductCardProps) => {
    const t = useTranslation();

    const media = content[0]?.media;
    const [visibleRemoveForm, setVisibleRemoveForm] = useState(false);
    const onClickRemoveButton = () => setVisibleRemoveForm(true);
    const onCloseRemoveForm = useCallback(() => setVisibleRemoveForm(false), []);
    const onClickRemoveForm = useCallback(() => {
        onProductRemove(id);
        onCloseRemoveForm();
    }, [id, onProductRemove, onCloseRemoveForm]);

    return (
        <Box pb={1} pr={1}>
            <Box
                backgroundColor="var(--control-secondary-background)"
                borderRadius={3}
                overflow="hidden"
                position="relative"
            >
                <Box
                    as="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    textDecoration="none"
                    href={addProtocol(url)}
                    onClick={onProductClick}
                >
                    <MediaCover
                        ratio={1}
                        overflow="hidden"
                        borderRadius="24px"
                        border="solid 4px transparent"
                        backgroundColor="unset"
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            backgroundColor="var(--background-primary)"
                        >
                            {media ? (
                                <Image
                                    objectFit="contain"
                                    width="100%"
                                    height="100%"
                                    src={buildImageSource(media)}
                                    srcSet={buildImageSourceSet(media)}
                                />
                            ) : (
                                <IconProduct
                                    size={72}
                                    color="var(--control-secondary-background)"
                                />
                            )}
                        </Flex>
                    </MediaCover>
                    <Box p={1} minHeight={72}>
                        <Title variant="body2" weight="regular">
                            {t(title)}
                        </Title>
                        <Price
                            priceInfo={priceInfo}
                            oldPriceInfo={oldPriceInfo}
                            fintechDiscount={fintechDiscount}
                            variant="body"
                            fintechDiscountOptions={fintechDiscountOptions}
                            hideOldPrice={hideOldPrice}
                            showOldPriceBadge={showOldPriceBadge}
                        />
                    </Box>
                </Box>
                {canRemoveProduct ? (
                    <RemoveButton variant="filled" onClick={onClickRemoveButton} size="xs">
                        <IconClose size={16} />
                    </RemoveButton>
                ) : null}
                {visibleRemoveForm ? (
                    <ProductRemoveForm onClose={onCloseRemoveForm} onRemove={onClickRemoveForm} />
                ) : null}
            </Box>
        </Box>
    );
};
